import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { ragChatService } from '@/services/ragChatService'

// 類型定義
export interface ChatMessageSourceMetadata {
  entity_type?: 'issue' | 'comment' | 'attachment' | 'worklog' | string
  issue_url?: string
  comment_url?: string
  download_url?: string
  entity_id?: string
  issue_id?: string
  issue_key?: string
  issue_title?: string
  project_name?: string
  entity_key?: string
  entity_title?: string
  parent_entity_key?: string
  original_name?: string
  filename?: string
}

export interface ChatMessageSource {
  content: string
  score?: number
  metadata?: ChatMessageSourceMetadata
}

export interface ChatMessageMetadata {
  sources?: ChatMessageSource[]
  processing_time?: number
  query_mode?: string
}

export interface ChatMessage {
  id: string
  content: string
  role: 'user' | 'assistant'
  timestamp: string  // 改為字符串以避免序列化問題
  metadata?: ChatMessageMetadata
}

export interface ChatSession {
  id: string
  name: string
  userId: string
  projectId?: string
  context?: {
    currentEntity?: {
      type: 'project' | 'issue' | 'comment' | 'worklog'
      id: string
      title?: string
    }
    userContext: {
      userId: string
      projectIds: string[]
      recentActions: Array<{
        action: string
        entityType: string
        entityId: string
        timestamp: string  // 改為字符串
      }>
    }
    pageContext?: {
      route: string
      section: string
      breadcrumbs: string[]
    }
  }
  createdAt: string  // 改為字符串以避免序列化問題
  updatedAt: string  // 改為字符串以避免序列化問題
}

export interface ChatSuggestion {
  id: string
  text: string
  category: 'recent' | 'context' | 'common'
  icon?: string
}

interface RagChatState {
  // 當前聊天狀態
  isOpen: boolean
  isMinimized: boolean
  
  // 會話管理
  currentSession: ChatSession | null
  sessions: ChatSession[]
  
  // 訊息相關
  messages: ChatMessage[]
  isTyping: boolean
  isLoading: boolean
  
  // 輸入相關
  inputValue: string
  suggestions: ChatSuggestion[]
  showSuggestions: boolean
  
  // 上下文感知
  currentContext: ChatSession['context'] | null
  
  // 錯誤處理
  error: string | null
  
  // UI 狀態
  position: { x: number; y: number }
  size: { width: number; height: number }
}

const initialState: RagChatState = {
  isOpen: false,
  isMinimized: false,
  
  currentSession: null,
  sessions: [],
  
  messages: [],
  isTyping: false,
  isLoading: false,
  
  inputValue: '',
  suggestions: [],
  showSuggestions: false,
  
  currentContext: null,
  
  error: null,
  
  position: { x: 20, y: 20 },
  size: { width: 450, height: 600 }
}

// 異步 thunks
export const sendMessage = createAsyncThunk(
  'ragChat/sendMessage',
  async (payload: { 
    message: string
    sessionId?: string
    context?: ChatSession['context']
    mode?: 'smart' | 'lightrag' | 'context-aware'
  }) => {
    const { message, sessionId, context, mode = 'smart' } = payload
    
    // 發送訊息到 RAG 服務
    const response = await ragChatService.sendMessage({
      query: message,
      sessionId,
      context,
      mode
    })
    
    return {
      userMessage: {
        id: `user-${Date.now()}`,
        content: message,
        role: 'user' as const,
        timestamp: new Date().toISOString()
      },
      assistantMessage: {
        id: `assistant-${Date.now()}`,
        content: response.answer,
        role: 'assistant' as const,
        timestamp: new Date().toISOString(),
        metadata: {
          sources: response.sources,
          processing_time: response.processing_time,
          query_mode: response.mode
        }
      }
    }
  }
)

export const createChatSession = createAsyncThunk(
  'ragChat/createSession',
  async (payload: {
    name?: string
    projectId?: string
    context?: ChatSession['context']
  }) => {
    console.log('Redux: Creating new chat session with payload:', payload)
    const response = await ragChatService.createSession(payload)
    console.log('Redux: Created session:', response)
    return response
  }
)

export const loadChatSessions = createAsyncThunk(
  'ragChat/loadSessions',
  async () => {
    console.log('Redux: Loading chat sessions...')
    const response = await ragChatService.getSessions()
    console.log('Redux: Loaded sessions:', response)
    return response
  }
)

export const loadChatMessages = createAsyncThunk(
  'ragChat/loadMessages',
  async (sessionId: string) => {
    const response = await ragChatService.getMessages(sessionId)
    return response
  }
)

export const generateSuggestions = createAsyncThunk(
  'ragChat/generateSuggestions',
  async (context: ChatSession['context']) => {
    const suggestions = await ragChatService.generateSuggestions(context)
    return suggestions
  }
)

// Slice 定義
const ragChatSlice = createSlice({
  name: 'ragChat',
  initialState,
  reducers: {
    // UI 控制
    toggleChat: (state) => {
      state.isOpen = !state.isOpen
      if (state.isOpen && state.isMinimized) {
        state.isMinimized = false
      }
    },
    
    openChat: (state) => {
      state.isOpen = true
      state.isMinimized = false
    },
    
    closeChat: (state) => {
      state.isOpen = false
    },
    
    minimizeChat: (state) => {
      state.isMinimized = true
    },
    
    maximizeChat: (state) => {
      state.isMinimized = false
    },
    
    // 位置和大小控制
    updatePosition: (state, action: PayloadAction<{ x: number; y: number }>) => {
      state.position = action.payload
    },
    
    updateSize: (state, action: PayloadAction<{ width: number; height: number }>) => {
      state.size = action.payload
    },
    
    // 輸入控制
    updateInputValue: (state, action: PayloadAction<string>) => {
      state.inputValue = action.payload
    },
    
    clearInput: (state) => {
      state.inputValue = ''
    },
    
    // 建議控制
    showSuggestions: (state) => {
      state.showSuggestions = true
    },
    
    hideSuggestions: (state) => {
      state.showSuggestions = false
    },
    
    applySuggestion: (state, action: PayloadAction<string>) => {
      state.inputValue = action.payload
      state.showSuggestions = false
    },
    
    // 上下文更新
    updateContext: (state, action: PayloadAction<ChatSession['context']>) => {
      state.currentContext = action.payload
    },
    
    // 會話控制
    switchSession: (state, action: PayloadAction<string>) => {
      const session = state.sessions.find(s => s.id === action.payload)
      if (session) {
        state.currentSession = session
        state.messages = [] // 清空當前訊息，觸發重新載入
      }
    },
    
    setCurrentSession: (state, action: PayloadAction<ChatSession>) => {
      state.currentSession = action.payload
      state.messages = [] // 清空當前訊息，觸發重新載入
    },
    
    // 訊息控制
    addMessage: (state, action: PayloadAction<ChatMessage>) => {
      state.messages.push(action.payload)
      
      // 保存訊息到本地存儲
      if (state.currentSession) {
        ragChatService.saveMessages(state.currentSession.id, state.messages)
      }
    },
    
    clearMessages: (state) => {
      state.messages = []
    },
    
    // 打字狀態
    setTyping: (state, action: PayloadAction<boolean>) => {
      state.isTyping = action.payload
    },
    
    // 錯誤處理
    clearError: (state) => {
      state.error = null
    }
  },
  
  extraReducers: (builder) => {
    // 發送訊息
    builder
      .addCase(sendMessage.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.isLoading = false
        // 添加用戶訊息和助手回覆
        state.messages.push(action.payload.userMessage)
        state.messages.push(action.payload.assistantMessage)
        state.inputValue = ''
        
        // 保存訊息到本地存儲
        if (state.currentSession) {
          ragChatService.saveMessages(state.currentSession.id, state.messages)
        }
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || '發送訊息失敗'
      })
    
    // 建立會話
    builder
      .addCase(createChatSession.fulfilled, (state, action) => {
        state.currentSession = action.payload
        state.sessions.unshift(action.payload)
        state.messages = []
      })
    
    // 載入會話列表
    builder
      .addCase(loadChatSessions.fulfilled, (state, action) => {
        state.sessions = action.payload
      })
    
    // 載入訊息
    builder
      .addCase(loadChatMessages.fulfilled, (state, action) => {
        state.messages = action.payload
      })
    
    // 生成建議
    builder
      .addCase(generateSuggestions.fulfilled, (state, action) => {
        state.suggestions = action.payload
      })
  }
})

export const {
  toggleChat,
  openChat,
  closeChat,
  minimizeChat,
  maximizeChat,
  updatePosition,
  updateSize,
  updateInputValue,
  clearInput,
  showSuggestions,
  hideSuggestions,
  applySuggestion,
  updateContext,
  switchSession,
  setCurrentSession,
  addMessage,
  clearMessages,
  setTyping,
  clearError
} = ragChatSlice.actions

export default ragChatSlice.reducer
